import { Link, useLocation } from "react-router-dom";

import styles from "./products.module.css";

/**
 * The suite, before the sign-in — DentaCore and the three that are coming.
 *
 * ── Why a page rather than a link on the sign-in screen ─────────────────────
 *
 * The client's framing was *"think like Netflix but I select before login"*:
 * the choice is which product you are here for, and it is made before anyone
 * asks who you are. Putting it after the sign-in would mean the account decides
 * the product, which is backwards — one person may eventually hold accounts in
 * two of them.
 *
 * ── Only DentaCore goes anywhere, and the other three say so ────────────────
 *
 * DermaCore, PedaCore and PsychoCore are named because the suite is the point,
 * and they are visibly inert because a tile that looks live and does nothing is
 * worse than one that admits it. They are not links, they are not focusable,
 * and they carry the word "Soon" rather than only a colour — a disabled state
 * signalled by grey alone is invisible to a third of readers in some lighting,
 * and to anyone who cannot distinguish it.
 *
 * ── Where you were headed survives the detour ───────────────────────────────
 *
 * `RequireAuth` sends a signed-out visitor here with the route they wanted, and
 * this hands it on to `/login`, which lands them there after signing in. Losing
 * it would mean a bookmarked patient file always dumped you on Home.
 */
export function ProductsPage(): React.JSX.Element {
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from;

  return (
    /*
     * **Laid out like a streaming service's profile picker** (FD-D3, Emile:
     * "my inspiration is the netflix account selection page"): the mark top
     * left, one question in the middle, a row of squares under it, each named
     * beneath. The layout only; the mark and the colours are DentaCore's own.
     */
    <main className={styles.page}>
      <header className={styles.brand}>
        <Tooth size={26} />
        <span>DentaCore</span>
      </header>

      <section className={styles.centre}>
        <h1 className={styles.title}>Which one are you here for?</h1>

        <ul className={styles.row}>
          <li>
            <Link
              to="/login"
              state={from === undefined ? undefined : { from }}
              className={styles.tile}
              title={DENTACORE_ABOUT}
              data-testid="product-dentacore"
            >
              <span
                className={`${styles.square ?? ""} ${styles.squareLive ?? ""}`}
                aria-hidden="true"
              >
                <Tooth size={72} />
              </span>
              <span className={styles.name}>DentaCore</span>
              <span className={styles.blurb}>Dental practice management</span>
            </Link>
          </li>

          {COMING.map((product) => (
            <li key={product.name}>
              {/*
                A `div`, not a disabled link. A disabled anchor is still in the
                tab order in several browsers, and announcing "link, DermaCore"
                to a screen reader for something that goes nowhere is a small lie.
              */}
              <div
                className={`${styles.tile ?? ""} ${styles.soon ?? ""}`}
                title={product.about}
                data-testid={`product-${product.name.toLowerCase()}`}
              >
                <span className={styles.square} aria-hidden="true">
                  <span className={styles.monogram}>{product.name.slice(0, 2)}</span>
                  <span className={styles.badge}>Soon</span>
                </span>
                <span className={styles.name}>{product.name}</span>
                <span className={styles.blurb}>{product.blurb}</span>
              </div>
            </li>
          ))}
        </ul>

        <p className={styles.foot}>
          No account? You need one before you can sign in &mdash; whoever runs your practice creates
          it. If you are setting a practice up yourself, <Link to="/signup">start here</Link>.
        </p>
      </section>
    </main>
  );
}

/**
 * The tooth, drawn rather than an emoji: an emoji renders as a different
 * picture on every platform and as a box on some. The mark top left and the
 * DentaCore square both use it.
 */
function Tooth({ size }: { size: number }): React.JSX.Element {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} role="presentation">
      <path
        d="M12 3c-2 0-2.6-1-4.6-1C5 2 3.5 4 3.5 7c0 2.6.9 4.2 1.5 6.4.5 1.9.6 4.1 1.1 5.8.3 1 .8 1.8 1.6 1.8 1.2 0 1.5-1.4 1.8-3 .3-1.5.5-2.8 1.5-2.8s1.2 1.3 1.5 2.8c.3 1.6.6 3 1.8 3 .8 0 1.3-.8 1.6-1.8.5-1.7.6-3.9 1.1-5.8.6-2.2 1.5-3.8 1.5-6.4 0-3-1.5-5-3.9-5C14.6 2 14 3 12 3Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * The three that do not exist yet.
 *
 * Named here rather than fetched: they are the product line, not a clinic's
 * data, and there is nothing to fetch them from. When one becomes real it gains
 * a route and moves out of this list.
 */
const COMING = [
  {
    name: "DermaCore",
    blurb: "Dermatology",
    about:
      "Will be used by dermatology clinics for appointments, patient records and treatment follow-ups.",
  },
  {
    name: "PedaCore",
    blurb: "Paediatrics",
    about:
      "Will be used by paediatric practices for appointments, growth and vaccination records, and family contacts.",
  },
  {
    name: "PsychoCore",
    blurb: "Psychiatry and psychology",
    about:
      "Will be used by psychiatrists and psychologists for sessions, confidential notes and follow-up plans.",
  },
];

/**
 * **Each product says what it is for** — on hover, as the tile's title, since FD-D3 (FD-D1, Emile, 2026-10-21: *"long
 * rectangles where each one describes the product"*). Product copy, not
 * clinic data — the same for every visitor.
 */
const DENTACORE_ABOUT =
  "Used by dentists and their teams to run the practice: the day's calendar across chairs and locations, patient files with tooth charts, treatment plans, staff rotas and stock.";
